import { createHash } from "node:crypto";
import type { FabricPrincipal } from "@fabric-harness/sdk";
import {
  databricksCredentials,
  databricksIdentity,
  fabricPrincipalFor,
  onBehalfOfFromHeaders,
} from "./identity.js";
import { createDatabricksScimClient } from "./native-sdk.js";

export interface DatabricksAppUserAuthorizationInspection {
  principal: FabricPrincipal;
  workspaceIdentity: {
    id: string;
    userName?: string;
    displayName?: string;
  };
  token: {
    issuedAt?: string;
    expiresAt?: string;
    remainingSeconds?: number;
  };
}

export interface DatabricksAppAuthenticatedPrincipal {
  id: string;
  kind: "user" | "service-principal" | "app-principal";
  displayName?: string;
  provider: "databricks-apps";
  tenantId: string;
  permissions: string[];
  ucPrincipal?: string;
}

export interface DatabricksAppUserAuthenticatorOptions {
  host: string;
  fetchImpl?: typeof fetch;
  /** Maximum validation-cache lifetime. It is always bounded by token expiry. Default 5 minutes. */
  cacheTtlMs?: number;
  /** Override the default per-user tenant mapping for an explicitly shared application tenant. */
  tenantId?: (inspection: DatabricksAppUserAuthorizationInspection) => string;
  /** Override the least-privilege permissions granted to an authenticated App user. */
  permissions?: string[];
  /**
   * Trust Databricks Apps reverse-proxy user identity headers when this authenticator is used only
   * behind Databricks Apps ingress. This authenticates the caller's workspace identity without
   * granting an on-behalf-of token to downstream code.
   */
  trustForwardedUserIdentity?: boolean;
  /**
   * Trust Databricks Apps reverse-proxy identity headers for M2M API calls that do not carry an
   * OBO user token. Enable only when this server is exclusively behind Databricks Apps ingress.
   */
  trustForwardedServicePrincipal?: boolean;
  /**
   * Treat requests without an OBO user token as App authorization and bind them to this App's
   * injected service principal. Enable only behind Databricks Apps ingress.
   */
  appPrincipalId?: string;
  /**
   * Service-principal IDs allowed to use App authorization when Databricks forwards their M2M
   * token through the OBO header. Values are matched against proxy identity headers and safe JWT
   * identity claims. Other forwarded tokens remain on the fail-closed OBO path.
   */
  appAuthorizedCallerIds?: string[];
  now?: () => number;
}

export const DATABRICKS_APP_USER_PERMISSIONS = [
  "agent:invoke",
  "approval:read",
  "approval:write",
  "artifact:read",
  "mcp:invoke",
  "session:abort",
  "session:delete",
  "session:read",
] as const;

/**
 * Authenticate Databricks Apps forwarded user tokens and isolate each user by default.
 * Validation calls the workspace current-user API once per token digest; raw tokens are never
 * stored as cache keys or returned to Fabric Harness.
 */
export function createDatabricksAppUserAuthenticator(
  options: DatabricksAppUserAuthenticatorOptions,
): (request: {
  headers: Headers | Record<string, string | string[] | undefined>;
}) => Promise<DatabricksAppAuthenticatedPrincipal | false | undefined> {
  const cacheTtlMs = options.cacheTtlMs ?? 5 * 60_000;
  if (!Number.isFinite(cacheTtlMs) || cacheTtlMs <= 0)
    throw new Error("cacheTtlMs must be a positive finite number.");
  const permissions = [...(options.permissions ?? DATABRICKS_APP_USER_PERMISSIONS)];
  const appAuthorizedCallerIds = new Set(
    (options.appAuthorizedCallerIds ?? []).map((value) => value.trim()).filter(Boolean),
  );
  const cache = new Map<
    string,
    { expiresAt: number; principal: DatabricksAppAuthenticatedPrincipal }
  >();

  return async (request) => {
    if (options.trustForwardedUserIdentity) {
      const forwardedUser = forwardedUserIdentity(request.headers);
      if (forwardedUser) {
        return {
          id: forwardedUser.id,
          kind: "user",
          provider: "databricks-apps",
          tenantId: databricksPrincipalTenantId("user", forwardedUser.id),
          permissions: [...permissions],
          ...(forwardedUser.displayName ? { displayName: forwardedUser.displayName } : {}),
          ...(forwardedUser.ucPrincipal ? { ucPrincipal: forwardedUser.ucPrincipal } : {}),
        };
      }
    }
    const delegated = onBehalfOfFromHeaders(request.headers);
    const now = options.now?.() ?? Date.now();
    const appPrincipal = options.appPrincipalId?.trim();
    if (
      delegated &&
      appPrincipal &&
      appAuthorizedCallerIds.size > 0 &&
      (await isAppAuthorizedCaller(request.headers, delegated, appAuthorizedCallerIds))
    ) {
      return appPrincipalFor(appPrincipal, permissions);
    }
    if (!delegated) {
      const forwarded = options.trustForwardedServicePrincipal
        ? forwardedIdentity(request.headers)
        : undefined;
      const id = forwarded ?? appPrincipal;
      if (!id) return undefined;
      if (!forwarded) return appPrincipalFor(id, permissions);
      return {
        id,
        kind: "service-principal",
        provider: "databricks-apps",
        tenantId: databricksPrincipalTenantId("service-principal", id),
        permissions: [...permissions],
      };
    }
    const token = await databricksIdentity(delegated)();
    const digest = createHash("sha256").update(token).digest("hex");
    const cached = cache.get(digest);
    if (cached && cached.expiresAt > now) return cached.principal;

    try {
      const inspection = await inspectDatabricksAppUserAuthorization({
        headers: request.headers,
        host: options.host,
        ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
        ...(options.now ? { now: options.now } : {}),
      });
      const id = inspection.principal.id;
      const tenantId = options.tenantId?.(inspection) ?? databricksPrincipalTenantId("user", id);
      if (!tenantId) return false;
      const principal: DatabricksAppAuthenticatedPrincipal = {
        id,
        kind: "user",
        provider: "databricks-apps",
        tenantId,
        permissions: [...permissions],
        ...(inspection.principal.displayName
          ? { displayName: inspection.principal.displayName }
          : {}),
        ...(inspection.principal.ucPrincipal
          ? { ucPrincipal: inspection.principal.ucPrincipal }
          : {}),
      };
      const tokenExpiry = inspection.token.expiresAt
        ? Date.parse(inspection.token.expiresAt) - 30_000
        : now + cacheTtlMs;
      cache.set(digest, {
        expiresAt: Math.max(now + 1, Math.min(now + cacheTtlMs, tokenExpiry)),
        principal,
      });
      return principal;
    } catch {
      cache.delete(digest);
      return false;
    }
  };
}

function appPrincipalFor(id: string, permissions: string[]): DatabricksAppAuthenticatedPrincipal {
  return {
    id,
    kind: "app-principal",
    provider: "databricks-apps",
    tenantId: databricksPrincipalTenantId("app", id),
    permissions: [...permissions],
  };
}

/** Stable, non-reversible tenant scope for Databricks App principals. */
export function databricksPrincipalTenantId(
  kind: "user" | "service-principal" | "app",
  id: string,
): string {
  return `databricks-${kind}:${createHash("sha256").update(id).digest("hex").slice(0, 24)}`;
}

async function isAppAuthorizedCaller(
  headers: Headers | Record<string, string | string[] | undefined>,
  delegated: Extract<ReturnType<typeof onBehalfOfFromHeaders>, { kind: "on-behalf-of" }>,
  allowed: Set<string>,
): Promise<boolean> {
  const forwarded = forwardedIdentity(headers);
  if (forwarded && allowed.has(forwarded)) return true;
  const claims = jwtIdentityClaims(await databricksIdentity(delegated)());
  return claims.some((value) => allowed.has(value));
}

function jwtIdentityClaims(token: string): string[] {
  const payload = token.split(".")[1];
  if (!payload) return [];
  try {
    const claims = JSON.parse(Buffer.from(payload, "base64url").toString("utf8")) as Record<
      string,
      unknown
    >;
    return ["sub", "client_id", "clientId", "azp", "oid"]
      .map((name) => claims[name])
      .filter((value): value is string => typeof value === "string" && value.length > 0);
  } catch {
    return [];
  }
}

function forwardedIdentity(
  headers: Headers | Record<string, string | string[] | undefined>,
): string | undefined {
  const read = (name: string): string | undefined => {
    if (typeof (headers as Headers).get === "function")
      return (headers as Headers).get(name) ?? undefined;
    const value = (headers as Record<string, string | string[] | undefined>)[name];
    return Array.isArray(value) ? value[0] : value;
  };
  return (
    (
      read("x-forwarded-user") ??
      read("x-forwarded-preferred-username") ??
      read("x-forwarded-email")
    )?.trim() || undefined
  );
}

function forwardedUserIdentity(
  headers: Headers | Record<string, string | string[] | undefined>,
): { id: string; displayName?: string; ucPrincipal?: string } | undefined {
  const read = (name: string): string | undefined => {
    if (typeof (headers as Headers).get === "function")
      return (headers as Headers).get(name)?.trim() || undefined;
    const value = (headers as Record<string, string | string[] | undefined>)[name];
    const first = Array.isArray(value) ? value[0] : value;
    return first?.trim() || undefined;
  };
  const email = read("x-forwarded-email");
  const preferredUsername = read("x-forwarded-preferred-username");
  // Databricks Apps also uses x-forwarded-user for service-principal ingress. Require a
  // natural-person marker before taking the trusted-user path so enabling both trust modes cannot
  // reinterpret an M2M caller as a user. Some workspaces omit x-forwarded-user for browser traffic,
  // so fall back to the integrity-protected email/username supplied by Apps ingress.
  const naturalPersonMarker = email ?? preferredUsername;
  if (!naturalPersonMarker) return undefined;
  const id = read("x-forwarded-user") ?? naturalPersonMarker;
  const ucPrincipal = naturalPersonMarker;
  return {
    id,
    ...(preferredUsername
      ? { displayName: preferredUsername }
      : email
        ? { displayName: email }
        : {}),
    ...(ucPrincipal ? { ucPrincipal } : {}),
  };
}

/**
 * Validate a Databricks Apps forwarded user token and return non-secret
 * identity/lifetime metadata suitable for OBO lifecycle certification.
 * The access token is never returned or included in an error.
 */
export async function inspectDatabricksAppUserAuthorization(options: {
  headers: Headers | Record<string, string | string[] | undefined>;
  host: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
}): Promise<DatabricksAppUserAuthorizationInspection> {
  const delegated = onBehalfOfFromHeaders(options.headers);
  if (!delegated) throw new Error("Databricks App request did not include user authorization.");
  const token = await databricksIdentity(delegated)();
  let identity: { id?: unknown; userName?: unknown; displayName?: unknown };
  try {
    identity = await createDatabricksScimClient({
      host: options.host,
      credentials: databricksCredentials(delegated),
      ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
    }).me({});
  } catch (error) {
    const status =
      typeof error === "object" && error !== null && "httpStatusCode" in error
        ? Number((error as { httpStatusCode?: unknown }).httpStatusCode)
        : undefined;
    throw new Error(
      `Databricks App user authorization validation returned ${Number.isFinite(status) ? status : "an error"}.`,
    );
  }
  const id =
    typeof identity.id === "string"
      ? identity.id
      : typeof identity.userName === "string"
        ? identity.userName
        : undefined;
  if (!id) throw new Error("Databricks current-user response did not include an identity.");

  const claims = jwtLifetimeClaims(token);
  const now = options.now?.() ?? Date.now();
  return {
    principal: fabricPrincipalFor(delegated, {
      id,
      ...(typeof identity.userName === "string" ? { ucPrincipal: identity.userName } : {}),
      ...(typeof identity.displayName === "string" ? { displayName: identity.displayName } : {}),
    }),
    workspaceIdentity: {
      id,
      ...(typeof identity.userName === "string" ? { userName: identity.userName } : {}),
      ...(typeof identity.displayName === "string" ? { displayName: identity.displayName } : {}),
    },
    token: {
      ...(claims.iat !== undefined ? { issuedAt: new Date(claims.iat * 1_000).toISOString() } : {}),
      ...(claims.exp !== undefined
        ? { expiresAt: new Date(claims.exp * 1_000).toISOString() }
        : {}),
      ...(claims.exp !== undefined
        ? { remainingSeconds: Math.max(0, Math.floor(claims.exp - now / 1_000)) }
        : {}),
    },
  };
}

function jwtLifetimeClaims(token: string): { iat?: number; exp?: number } {
  const payload = token.split(".")[1];
  if (!payload) return {};
  try {
    const value = JSON.parse(Buffer.from(payload, "base64url").toString("utf8")) as {
      iat?: unknown;
      exp?: unknown;
    };
    return {
      ...(typeof value.iat === "number" && Number.isFinite(value.iat) ? { iat: value.iat } : {}),
      ...(typeof value.exp === "number" && Number.isFinite(value.exp) ? { exp: value.exp } : {}),
    };
  } catch {
    return {};
  }
}
